import { useMaintenanceCheck, MaintenanceScreen } from '../MaintenanceGate';
import React, { useState, useEffect, useRef, useMemo, useTransition, useDeferredValue } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, regionsOf, regionLabel } from '../AuthContext';

// -- Standard reason translations ----------------------------------------------
// Exact-match: key = system-generated decisionRemark string.
const STANDARD_REASON_TRANSLATIONS = {
  'Dormant: no orders in 6 months': {
    en: 'Zero orders in past 6 months',
    bm: 'Tiada order dalam 6 bulan terlepas',
  },
  'Repayment tier = Fail (Petrol Mart floor RM200 applied)': {
    en: 'Repayment tier = Fail (Petrol Mart floor RM200 applied)',
    bm: 'Rekod bayaran gagal (Had kredit diturunkan ke paras minima petrolmart RM200)',
  },
  // When system emits plain "Repayment tier = Fail", sebabDisplay checks reviewedLimit===200
  // to route Petrolmart cases to the entry above. This entry covers genuine non-Petrolmart.
  'Repayment tier = Fail': {
    en: 'Repayment tier = Fail (Non petrolmart)',
    bm: 'Rekod bayaran gagal (Bukan Petrolmart)',
  },
  'Within 3-month cooling off': {
    en: '(No limit change) Within 3-month cooling off',
    bm: 'Tiada sekatan (Dalam tempoh percubaan 3 bulan)',
  },
  'Unused headroom vs P75 peak exposure': {
    en: 'Low credit utilization',
    bm: 'Kadar pengunaan kredit yang rendah',
  },
  'No trigger': {
    en: 'No changes',
    bm: 'Tiada Perubahan',
  },
};

// Pattern-match: for system remarks that include a variable suffix (date, count).
// Checked in order; first match wins.
const PATTERN_REASON_TRANSLATIONS = [
  {
    // e.g. "KYC expired 30-09-2024"
    prefix: 'KYC expired ',
    en: (suffix) => `KYC/ Biz License expired ${suffix}`,
    bm: (suffix) => `KYC/ Lesen Perniagaan lupus pada ${suffix}`,
  },
  {
    // e.g. "Peak utilisation >=85% in 3 of last 6 months"
    prefix: 'Peak utilisation >=85% in ',
    en: () => 'Satisfactory credit utilization with good repayment record',
    bm: () => 'Kadar penggunaan kredit yang tinggi serta bayar tepat pada masa',
  },
];

function sebabDisplay(remark, langKey, row) {
  if (!remark) return '';
  // Special case: system emits "Repayment tier = Fail" for both Petrolmart and non-Petrolmart
  // until the new flag ships. Route to the Petrolmart translation when reviewedLimit === 200.
  if (remark === 'Repayment tier = Fail' && row?.reviewedLimit === 200) {
    const tr = STANDARD_REASON_TRANSLATIONS['Repayment tier = Fail (Petrol Mart floor RM200 applied)'];
    if (tr) return tr[langKey] || tr.en || remark;
  }
  // 1. Exact match
  const exact = STANDARD_REASON_TRANSLATIONS[remark];
  if (exact) return exact[langKey] || exact.en || remark;
  // 2. Prefix / pattern match
  for (const p of PATTERN_REASON_TRANSLATIONS) {
    if (remark.startsWith(p.prefix)) {
      const suffix = remark.slice(p.prefix.length);
      const fn = p[langKey] || p.en;
      return fn ? fn(suffix) : remark;
    }
  }
  return remark;
}

// -- Language --------------------------------------------------
const LANG = {
  en: {
    title: 'Credit Application System',
    signOut: 'Sign out',
    pageTitle: (r) => `Monthly Credit Review (Region ${r})`,
    reviewMonth: 'Review Month',
    noReview: 'No monthly review has been published yet.',
    loading: 'Loading...',
    limitIncrease: 'Limit Increased',
    limitDecrease: 'Limit Decreased',
    limitRevoke:   'Limit Revoked',
    kycExpiring:   'KYC Expiring',
    outletId: 'Outlet ID',
    outletName: 'Outlet Name',
    vanId: 'Van ID',
    site: 'Site',
    limitBefore: 'Limit Before',
    limitAfter: 'Limit After',
    changes: 'Changes',
    decision: 'Decision',   // outcome badge column (was "Reason")
    reason: 'Reason',       // decisionRemark column (was "Approver Remark")
    region: 'Region',
    allRegions: 'All Regions',
    approver: 'Approver',
    downloadCsv: 'Download CSV',
    clearFilters: 'Reset',
    allSites: 'All Sites',
    allDecisions: 'All Decisions',
    allSebab: 'All Reasons',
    searchPlaceholder: 'Search...',
    reasonLabels: {
      INCREASE:      'Limit Increased',
      DECREASE:      'Limit Decreased',
      HARD_ZERO:     'Limit Revoked',
      PROPOSED_ZERO: 'Limit Revoked (KYC Expired)',
      FLAG_ONLY:     'KYC Expiring',
      NO_CHANGE:     'No Change',
    },
  },
  bm: {
    title: 'Sistem Permohonan Kredit',
    signOut: 'Log keluar',
    pageTitle: (r) => `Penilaian Kredit Bulanan (Region ${r})`,
    reviewMonth: 'Bulan Penilaian',
    noReview: 'Penilaian bulanan belum diterbitkan.',
    loading: 'Sedang dimuat turun',
    limitIncrease: 'Had Naik',
    limitDecrease: 'Had Kurang',
    limitRevoke:   'Had dibatalkan',
    kycExpiring:   'KYC/Lesen Perniagaan Hampir Lupus',
    outletId: 'ID Outlet',
    outletName: 'Nama Outlet',
    vanId: 'ID Van',
    site: 'Site',
    limitBefore: 'Had Terdahulu',
    limitAfter: 'Had Baharu',
    changes: 'Perubahan',
    decision: 'Keputusan',  // outcome badge column (was "Sebab")
    reason: 'Sebab',        // decisionRemark column (was "Nota Pelulus")
    region: 'Region',
    allRegions: 'Semua Region',
    approver: 'Pelulus',
    downloadCsv: 'Muat Turun CSV',
    clearFilters: 'Set Semula',
    allSites: 'Semua Site',
    allDecisions: 'Semua Keputusan',
    allSebab: 'Semua Sebab',
    searchPlaceholder: 'Carian...',
    reasonLabels: {
      INCREASE:      'Had Dinaikkan',
      DECREASE:      'Had Dikurangkan',
      HARD_ZERO:     'Had Dibatalkan',
      PROPOSED_ZERO: 'Had Dibatalkan (KYC/Lesen Perniagaan Lupus)',
      FLAG_ONLY:     'KYC Hampir Lupus',
      NO_CHANGE:     'Tiada Perubahan',
    },
  },
};

function fmt(n) {
  const num = Number(n);
  if (isNaN(num)) return '�';
  return num.toLocaleString('en-MY');
}

function getCASUserTitle(email, role) {
  const RSH_EMAILS = ['rosvinder.kaur@ninjavan.co', 'afiq.hadi@ninjavan.co'];
  const HEAD_OF_SALES_EMAILS = ['dwayne.tung@ninjavan.co'];
  if (role !== 'sales') return '';
  if (HEAD_OF_SALES_EMAILS.includes(email)) return 'Deputy Head, Ninja Mart';
  return RSH_EMAILS.includes(email) ? 'Regional Sales Head' : 'Regional Sales Supervisor';
}

// -- CSV export ----------------------------------------------------------------
function exportCSV(rows, t, langKey, filename) {
  const headers = [
    t.outletId, t.outletName, t.vanId, t.site,
    t.limitBefore, t.limitAfter, t.changes,
    t.decision, t.reason, t.approver,
    'Remark',
  ];
  const escape = (v) => {
    const s = v == null ? '' : String(v);
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };
  const csvLines = [
    headers.map(escape).join(','),
    ...rows.map(r => [
      r.outletId   || '',
      r.outletName || '',
      r.vanId      || '',
      r.site       || '',
      r.currentLimit  ?? '',
      r.reviewedLimit ?? '',
      r.finalDelta    ?? '',
      t.reasonLabels[r.outcome] || r.outcome || '',
      sebabDisplay(r.decisionRemark, langKey, r) || '',
      r.reviewer   || '',
      '',   // Remark � blank column for manual notes
    ].map(escape).join(',')),
  ];
  const blob = new Blob(['?' + csvLines.join('\r\n')], { type: 'text/csv;charset=utf-8;' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// -- Sidebar -------------------------------------------------------------------
function MonthlyReviewSidebar({ open, onClose, navigate, lang }) {
  if (!open) return null;
  const items = [
    { label: lang === 'bm' ? 'Ringkasan Kredit & Tunggakan' : 'AR Summary',          path: '/cas/ar',             active: false },
    { label: lang === 'bm' ? 'Semakan Permohonan Kredit'    : 'Credit Applications', path: '/cas/dashboard',      active: false },
    { label: lang === 'bm' ? 'Penilaian Kredit Bulanan'     : 'Monthly Review',      path: '/cas/monthly-review', active: true  },
  ];
  return (
    <>
      <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 300 }} onClick={onClose} />
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: 240, background: '#fff', zIndex: 400, boxShadow: '4px 0 16px rgba(0,0,0,0.15)', display: 'flex', flexDirection: 'column' }}>
        <div style={{ background: '#37474f', padding: '20px 16px 16px', color: '#fff' }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>Ninja Mart Credit</div>
          <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Sales Portal</div>
        </div>
        <div style={{ flex: 1, padding: '12px 0' }}>
          {items.map(item => (
            <div key={item.path}
              style={{ padding: '12px 20px', cursor: 'pointer', fontSize: 13, fontWeight: item.active ? 700 : 400, color: item.active ? '#37474f' : '#555', background: item.active ? '#eceff1' : 'transparent', borderLeft: item.active ? '3px solid #37474f' : '3px solid transparent' }}
              onClick={e => { e.stopPropagation(); navigate(item.path); onClose(); }}>
              {item.label}
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

// nav � 52px tall (12px padding � 2 + 28px logo)
// filter bar � 82px tall (14px padding � 2 + 18px label + 4px gap + 36px input)
const NAV_H        = 52;
const FILTER_BAR_H = 82;

const s = {
  page:       { minHeight: '100vh', background: '#f0f2f5', fontFamily: 'system-ui,-apple-system,sans-serif' },
  nav:        { background: '#37474f', padding: '12px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', position: 'sticky', top: 0, zIndex: 100 },
  navL:       { display: 'flex', alignItems: 'center', gap: 12 },
  navT:       { color: '#fff', fontWeight: 700, fontSize: 15 },
  navR:       { display: 'flex', alignItems: 'center', gap: 12 },
  langToggle: { background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.4)', color: '#fff', padding: '4px 10px', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 },
  body:       { maxWidth: 1200, margin: '0 auto', padding: '20px 16px' },
  header:     { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  h1:         { fontSize: 18, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  monthPill:  { background: '#c5221f', borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 700, color: '#fff', boxShadow: '0 2px 8px rgba(197,34,31,0.35)' },
  monthLabel: { fontSize: 10, fontWeight: 600, opacity: 0.85, textTransform: 'uppercase', letterSpacing: '0.5px' },
  monthValue: { fontSize: 16, fontWeight: 700, marginTop: 1 },
  stats:      { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 10, marginBottom: 16 },
  stat:       { background: '#fff', borderRadius: 10, padding: '14px 18px', boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  statN:      { fontSize: 26, fontWeight: 700 },
  statL:      { fontSize: 11, color: '#888', marginTop: 2 },
  card:       { background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' },
  table:      { width: '100%', borderCollapse: 'collapse' },
  // th is sticky below nav + filter bar
  th:         { padding: '10px 12px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', borderBottom: '2px solid #e0e0e0', background: '#f8f9fa', whiteSpace: 'nowrap', position: 'sticky', top: NAV_H + FILTER_BAR_H, zIndex: 10 },
  td:         { padding: '11px 12px', fontSize: 13, borderBottom: '1px solid #f5f5f5', verticalAlign: 'middle' },
  trow:       { transition: 'background 0.1s' },
  badge:      { display: 'inline-block', padding: '3px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  empty:      { padding: 48, textAlign: 'center', color: '#888' },
  loadBox:    { padding: 48, textAlign: 'center', color: '#555' },
  spinner:    { display: 'inline-block', width: 24, height: 24, border: '3px solid #eee', borderTopColor: '#37474f', borderRadius: '50%', animation: 'spin 0.7s linear infinite', marginBottom: 12 },
  // Filter bar � sticks directly under the nav
  filterBar:   { background: '#fff', borderRadius: 10, boxShadow: '0 2px 8px rgba(0,0,0,0.10)', padding: '12px 16px', marginBottom: 16, position: 'sticky', top: NAV_H, zIndex: 50 },
  filterRow:   { display: 'flex', flexWrap: 'nowrap', gap: 8, alignItems: 'flex-end', overflow: 'hidden' },
  filterGroup: { display: 'flex', flexDirection: 'column', gap: 3, flex: '0 0 auto' },
  filterLabel: { fontSize: 10, fontWeight: 700, color: '#888', textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' },
  filterInput: { padding: '5px 8px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13, outline: 'none', width: '100%', boxSizing: 'border-box' },
  filterSelect:{ padding: '5px 8px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 13, outline: 'none', width: '100%', background: '#fff', boxSizing: 'border-box', overflow: 'hidden', textOverflow: 'ellipsis' },
  clearBtn:    { padding: '6px 14px', border: '1px solid #e0e0e0', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: '#fff', color: '#555', fontWeight: 600, whiteSpace: 'nowrap' },
  csvBtn:      { padding: '6px 14px', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer', background: '#37474f', color: '#fff', fontWeight: 700, whiteSpace: 'nowrap' },
};

const SECTION_ORDER = ['INCREASE', 'DECREASE', 'HARD_ZERO', 'PROPOSED_ZERO', 'FLAG_ONLY', 'NO_CHANGE'];
const SECTION_COLOR = {
  INCREASE:      '#1e7e34',
  DECREASE:      '#b06000',
  HARD_ZERO:     '#c5221f',
  PROPOSED_ZERO: '#c5221f',
  FLAG_ONLY:     '#1565c0',
  NO_CHANGE:     '#888888',
};

export default function MonthlyReview() {
  const { user, logout } = useAuth();
  const maint = useMaintenanceCheck('sales');
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => localStorage.getItem('cas_lang') || 'en');
  const deferredLang = useDeferredValue(lang);
  const [, startTransition] = useTransition();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const t = LANG[lang];           // nav/buttons use live lang � instant
  const tDeferred = LANG[deferredLang]; // table rows use deferred lang � non-blocking

  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [data, setData]       = useState(null);

  // -- Filters --------------------------------------------------
  const [filterOutletId,   setFilterOutletId]   = useState('');
  const [filterOutletName, setFilterOutletName] = useState('');
  const [filterVanId,      setFilterVanId]      = useState('');
  const [filterSite,       setFilterSite]       = useState('');
  const [filterDecision,   setFilterDecision]   = useState('');
  const [filterSebab,      setFilterSebab]      = useState('');
  const [filterRegion,     setFilterRegion]     = useState('');

  useEffect(() => {
    function onDocClick(e) {
      if (profileRef.current && !profileRef.current.contains(e.target)) setProfileOpen(false);
    }
    document.addEventListener('click', onDocClick);
    return () => document.removeEventListener('click', onDocClick);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError('');
      try {
                const res = await fetch(`/api/cas/monthly-review?region=${encodeURIComponent(regionsOf(user).join(','))}`);
        const json = await res.json();
        if (cancelled) return;
        if (!res.ok) { setError(json.error || 'Failed to load'); setData(null); }
        else setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [user.region]);

  function toggleLang() {
    const next = lang === 'en' ? 'bm' : 'en';
    localStorage.setItem('cas_lang', next);
    startTransition(() => setLang(next));
  }

  const rows = data?.rows || [];

  function outcomeOf(r) {
    if (r.systemAction === 'FLAG_ONLY') return 'FLAG_ONLY';
    if (r.reviewedLimit === 0 && r.currentLimit > 0) {
      return r.systemAction === 'PROPOSED_ZERO' ? 'PROPOSED_ZERO' : 'HARD_ZERO';
    }
    if (r.finalDelta > 0) return 'INCREASE';
    if (r.finalDelta < 0) return 'DECREASE';
    return 'NO_CHANGE';
  }

  // Augment rows: outcome + derived site � only recalculates when data changes, not on lang toggle
  const rowsWithOutcome = useMemo(() => rows.map(r => ({
    ...r,
    outcome: outcomeOf(r),
    site:    r.vanId ? r.vanId.substring(1, 3) : '',
  })), [rows]);

  // Dropdown options � data-dependent only
  const uniqueSites     = useMemo(() => [...new Set(rowsWithOutcome.map(r => r.site).filter(Boolean))].sort(), [rowsWithOutcome]);
  const uniqueDecisions = useMemo(() => SECTION_ORDER.filter(o => rowsWithOutcome.some(r => r.outcome === o)), [rowsWithOutcome]);
  // Sebab sort uses lang � legitimately re-runs on lang change
  const uniqueSebabRemarks = useMemo(() =>
    [...new Set(rowsWithOutcome.map(r => r.decisionRemark).filter(Boolean))]
      .sort((a, b) => sebabDisplay(a, lang).localeCompare(sebabDisplay(b, lang))),
  [rowsWithOutcome, lang]);

  // Apply filters
  const hasFilter = filterOutletId || filterOutletName || filterVanId || filterSite || filterDecision || filterSebab || filterRegion;
  const regions = useMemo(() => [...new Set(rowsWithOutcome.map(r => r.region).filter(Boolean))], [rowsWithOutcome]);
  const filteredRows = useMemo(() => rowsWithOutcome.filter(r => {
    if (filterOutletId   && !r.outletId?.toLowerCase().includes(filterOutletId.toLowerCase()))    return false;
    if (filterOutletName && !r.outletName?.toLowerCase().includes(filterOutletName.toLowerCase())) return false;
    if (filterVanId      && !r.vanId?.toLowerCase().includes(filterVanId.toLowerCase()))           return false;
    if (filterSite       && r.site !== filterSite)        return false;
    if (filterDecision   && r.outcome !== filterDecision)  return false;
    if (filterSebab      && r.decisionRemark !== filterSebab) return false;
    if (filterRegion     && r.region !== filterRegion) return false;
    return true;
  }), [rowsWithOutcome, filterOutletId, filterOutletName, filterVanId, filterSite, filterDecision, filterSebab, filterRegion]);

  // Sort by outcome order then by date
  const sortedRows = useMemo(() => [...filteredRows].sort((a, b) => {
    const oi = SECTION_ORDER.indexOf(a.outcome);
    const bi = SECTION_ORDER.indexOf(b.outcome);
    if (oi !== bi) return oi - bi;
    return (b.submittedAt || '').localeCompare(a.submittedAt || '');
  }), [filteredRows]);

  // Stats � data-dependent only, never recalculate on lang toggle
  const { increaseRows, decreaseRows, revokeRows, counts, sums } = useMemo(() => {
    const inc = rowsWithOutcome.filter(r => r.outcome === 'INCREASE');
    const dec = rowsWithOutcome.filter(r => r.outcome === 'DECREASE');
    const rev = rowsWithOutcome.filter(r => r.outcome === 'HARD_ZERO' || r.outcome === 'PROPOSED_ZERO');
    return {
      increaseRows: inc,
      decreaseRows: dec,
      revokeRows:   rev,
      counts: {
        INCREASE:  inc.length,
        DECREASE:  dec.length,
        REVOKE:    rev.length,
        FLAG_ONLY: rowsWithOutcome.filter(r => r.outcome === 'FLAG_ONLY').length,
      },
      sums: {
        INCREASE: inc.reduce((s, r) => s + (r.finalDelta || 0), 0),
        DECREASE: dec.reduce((s, r) => s + Math.abs(r.finalDelta || 0), 0),
        REVOKE:   rev.reduce((s, r) => s + Math.abs(r.finalDelta || 0), 0),
      },
    };
  }, [rowsWithOutcome]);

  function clearFilters() {
    setFilterOutletId('');
    setFilterOutletName('');
    setFilterVanId('');
    setFilterSite('');
    setFilterDecision('');
    setFilterSebab('');
    setFilterRegion('');
  }

  function handleDownloadCSV() {
    const rowsToExport = hasFilter ? sortedRows : rowsWithOutcome;
    const month = data?.reviewMonth ? data.reviewMonth.replace(/[^a-zA-Z0-9]/g, '-') : 'monthly-review';
    exportCSV(rowsToExport, t, lang, `monthly-review-${month}.csv`);
  }

  if (maint.blocked) return <MaintenanceScreen />;
  return (
    <div style={s.page}>
      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        @media(max-width:600px){
          .mr-stats{grid-template-columns:repeat(2,1fr)!important;}
          .mr-filter-row{flex-wrap:wrap!important;overflow:visible!important;}
          .mr-filter-row>div{flex:1 1 calc(50% - 4px)!important;min-width:120px!important;}
          .mr-filter-row>div input,.mr-filter-row>div select{width:100%!important;box-sizing:border-box!important;}
          .mr-filter-row>div:last-child{flex:0 0 auto!important;}
          .mr-logo{display:none!important;}
          .mr-nav-title{display:none!important;}
          .mr-nav-sub{display:none!important;}
          .mr-h1{font-size:14px!important;}
          .mr-table-card{overflow:hidden!important;}
          .mr-table-scroll{overflow:auto!important;max-height:calc(100vh - 420px)!important;}
          .mr-table-scroll th{top:0!important;}
        }
      `}</style>
      <MonthlyReviewSidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} navigate={navigate} lang={lang} />

      <nav style={s.nav}>
        <div style={s.navL}>
          <button style={{ background: 'none', border: 'none', color: '#fff', fontSize: 20, cursor: 'pointer' }}
            onClick={() => setSidebarOpen(true)}>
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1, marginBottom:4 }} />
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1, marginBottom:4 }} />
            <span style={{ display:'block', width:18, height:2, background:'#fff', borderRadius:1 }} />
          </button>
          <img
            src="/logo-wordmark.svg"
            alt="Ninja Mart"
            height={28}
            style={{ height: 28, width: 'auto', objectFit: 'contain' }}
            className="mr-logo"
            onError={e => { e.target.style.display = 'none'; }} />
          <div style={s.navT} className="mr-nav-title">{t.title}</div>
        </div>
        <div style={s.navR}>
          <div style={{ position: 'relative' }} ref={profileRef}>
            <div style={{ textAlign: 'right', cursor: 'pointer', userSelect: 'none' }}
              onClick={e => { e.stopPropagation(); setProfileOpen(p => !p); }}>
              <div style={{ fontWeight: 600, color: '#fff', fontSize: 13 }}>{user.displayName}<span style={{ display:'inline-block', width:0, height:0, borderLeft:'4px solid transparent', borderRight:'4px solid transparent', borderTop:'5px solid #fff', marginLeft:5, verticalAlign:'middle' }} /></div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.7)', marginTop: 1 }} className="mr-nav-sub">{getCASUserTitle(user.email, user.role)} - {regionLabel(user)}</div>
            </div>
            {profileOpen && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: 8, background: '#fff', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 200, overflow: 'hidden' }}>
                <button style={{ width: '100%', padding: '12px 16px', background: 'none', border: 'none', textAlign: 'left', cursor: 'pointer', fontSize: 13, color: '#c5221f', fontWeight: 600 }}
                  onClick={e => { e.stopPropagation(); logout(); }}>{t.signOut}</button>
              </div>
            )}
          </div>
          <button style={s.langToggle} onClick={toggleLang}>
            <span style={{ opacity: lang === 'bm' ? 1 : 0.5 }}>BM</span>
            {' | '}
            <span style={{ opacity: lang === 'en' ? 1 : 0.5 }}>EN</span>
          </button>
        </div>
      </nav>

      <div style={s.body}>
        <div style={s.header}>
          <div style={s.h1} className="mr-h1">{t.pageTitle(regionLabel(user))}</div>
          {data?.reviewMonth && (
            <div style={s.monthPill}>
              <div style={s.monthLabel}>{t.reviewMonth}</div>
              <div style={s.monthValue}>{data.reviewMonth}</div>
            </div>
          )}
        </div>

        {loading ? (
          <div style={s.loadBox}><div style={s.spinner} /><div style={{ fontSize: 13 }}>{t.loading}</div></div>
        ) : error ? (
          <div style={{ ...s.empty, color: '#c5221f' }}>{error}</div>
        ) : !data?.found ? (
          <div style={s.empty}>{t.noReview}</div>
        ) : (
          <>
            {/* -- Summary stats -- */}
            <div style={s.stats} className="mr-stats">
              {[
                { n: counts.INCREASE,  l: t.limitIncrease, c: '#1e7e34', sum: sums.INCREASE  },
                { n: counts.DECREASE,  l: t.limitDecrease, c: '#b06000', sum: sums.DECREASE  },
                { n: counts.REVOKE,    l: t.limitRevoke,   c: '#c5221f', sum: sums.REVOKE    },
                { n: counts.FLAG_ONLY, l: t.kycExpiring,   c: '#1565c0', sum: null            },
              ].map((st, i) => (
                <div key={i} style={s.stat}>
                  <div style={{ ...s.statN, color: st.c }}>{st.n}</div>
                  <div style={s.statL}>{st.l}</div>
                  {st.sum != null && (
                    <div style={{ fontSize: 12, color: st.c, fontWeight: 600, marginTop: 4, opacity: 0.85 }}>
                      RM {fmt(st.sum)}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* -- Filter bar (sticky below nav) -- */}
            <div style={s.filterBar}>
              <div style={s.filterRow} className="mr-filter-row">
                <div style={{ ...s.filterGroup, width: 120 }}>
                  <span style={s.filterLabel}>{t.outletId}</span>
                  <input style={{ ...s.filterInput, width: 120 }} value={filterOutletId} onChange={e => setFilterOutletId(e.target.value)} placeholder={t.searchPlaceholder} />
                </div>
                <div style={{ ...s.filterGroup, width: 140 }}>
                  <span style={s.filterLabel}>{t.outletName}</span>
                  <input style={{ ...s.filterInput, width: 140 }} value={filterOutletName} onChange={e => setFilterOutletName(e.target.value)} placeholder={t.searchPlaceholder} />
                </div>
                <div style={{ ...s.filterGroup, width: 110 }}>
                  <span style={s.filterLabel}>{t.vanId}</span>
                  <input style={{ ...s.filterInput, width: 110 }} value={filterVanId} onChange={e => setFilterVanId(e.target.value)} placeholder={t.searchPlaceholder} />
                </div>
                <div style={{ ...s.filterGroup, width: 90 }}>
                  <span style={s.filterLabel}>{t.site}</span>
                  <select style={{ ...s.filterSelect, width: 90 }} value={filterSite} onChange={e => setFilterSite(e.target.value)}>
                    <option value="">{t.allSites}</option>
                    {uniqueSites.map(site => <option key={site} value={site}>{site}</option>)}
                  </select>
                </div>
                <div style={{ ...s.filterGroup, width: 150 }}>
                  <span style={s.filterLabel}>{t.decision}</span>
                  <select style={{ ...s.filterSelect, width: 150 }} value={filterDecision} onChange={e => setFilterDecision(e.target.value)}>
                    <option value="">{t.allDecisions}</option>
                    {uniqueDecisions.map(o => <option key={o} value={o}>{t.reasonLabels[o] || o}</option>)}
                  </select>
                </div>
                <div style={{ ...s.filterGroup, width: 170 }}>
                  <span style={s.filterLabel}>{t.reason}</span>
                  <select style={{ ...s.filterSelect, width: 170 }} value={filterSebab} onChange={e => setFilterSebab(e.target.value)}>
                    <option value="">{t.allSebab}</option>
                    {uniqueSebabRemarks.map(raw => <option key={raw} value={raw}>{sebabDisplay(raw, lang)}</option>)}
                  </select>
                </div>
                {regions.length > 1 && (
                  <div style={{ ...s.filterGroup, width: 130 }}>
                    <span style={s.filterLabel}>{t.region}</span>
                    <select style={{ ...s.filterSelect, width: 130 }} value={filterRegion} onChange={e => setFilterRegion(e.target.value)}>
                      <option value="">{t.allRegions}</option>
                      {regions.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, flexShrink: 0, marginLeft: 'auto' }}>
                  {hasFilter && (
                    <button style={s.clearBtn} onClick={clearFilters}>{t.clearFilters}</button>
                  )}
                  <button style={s.csvBtn} onClick={handleDownloadCSV}>{t.downloadCsv}</button>
                </div>
              </div>
            </div>

            {/* -- Single flat table -- */}
            {sortedRows.length === 0 ? (
              <div style={{ ...s.empty, background: '#fff', borderRadius: 10, boxShadow: '0 1px 4px rgba(0,0,0,0.07)' }}>{t.noReview}</div>
            ) : (
              <div style={s.card} className="mr-table-card">
              <div className="mr-table-scroll">
                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>{t.outletId}</th>
                        <th style={s.th}>{t.outletName}</th>
                        <th style={s.th}>{t.vanId}</th>
                        <th style={s.th}>{t.site}</th>
                        <th style={s.th}>{t.limitBefore}</th>
                        <th style={s.th}>{t.limitAfter}</th>
                        <th style={s.th}>{t.changes}</th>
                        <th style={s.th}>{t.decision}</th>
                        <th style={s.th}>{t.reason}</th>
                        <th style={s.th}>{t.approver}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedRows.map(r => {
                        const color = SECTION_COLOR[r.outcome] || '#888';
                        return (
                          <tr key={r.outletId} style={s.trow}
                            onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}>
                            <td style={s.td}><strong>{r.outletId}</strong></td>
                            <td style={s.td}>{r.outletName || '�'}</td>
                            <td style={s.td}>{r.vanId || '�'}</td>
                            <td style={{ ...s.td, color: '#666', fontSize: 12 }}>{r.site || '�'}</td>
                            <td style={s.td}>{fmt(r.currentLimit)}</td>
                            <td style={s.td}>{fmt(r.reviewedLimit)}</td>
                            <td style={{ ...s.td, color: r.finalDelta > 0 ? '#1e7e34' : r.finalDelta < 0 ? '#c5221f' : '#888', fontWeight: 600 }}>
                              {r.finalDelta > 0 ? '+' : ''}{fmt(r.finalDelta)}
                            </td>
                            <td style={s.td}>
                              <span style={{ ...s.badge, background: color + '22', color }}>
                                {tDeferred.reasonLabels[r.outcome] || r.outcome}
                              </span>
                            </td>
                            <td style={{ ...s.td, color: '#555', fontSize: 12 }}>
                              {sebabDisplay(r.decisionRemark, deferredLang, r) || '�'}
                            </td>
                            <td style={s.td}>{r.reviewer || '�'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
              </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}